import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PaperPlaneRight, ChatCircle } from 'phosphor-react';
import { db } from '../../firebase';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

// ============================================================================
// PLANNING CHAT WIDGET
// ============================================================================
// Embedded chat for the planning panel.
// Messages are stored in Firestore under:
//   connections/{connectionId}/planningChat/{messageId}
// ============================================================================

interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  createdAt: Date;
}

interface PlanningChatProps {
  connectionId: string;
  currentUserId: string;
  currentUserName: string;
  otherUserName?: string;
}

const PlanningChat: React.FC<PlanningChatProps> = ({
  connectionId,
  currentUserId,
  currentUserName,
  otherUserName = 'Connectie',
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Listen for messages
  useEffect(() => {
    if (!connectionId) return;

    const chatRef = collection(db, 'connections', connectionId, 'planningChat');
    const q = query(chatRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: ChatMessage[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        msgs.push({
          id: doc.id,
          text: data.text || '',
          senderId: data.senderId || '',
          senderName: data.senderName || '',
          createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate()
            : new Date(data.createdAt || Date.now()),
        });
      });
      setMessages(msgs);
    }, (error) => {
      console.error('[PlanningChat] Error listening to messages:', error);
    });

    return () => unsubscribe();
  }, [connectionId]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current && isExpanded) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isExpanded]);

  const handleSend = useCallback(async () => {
    const text = newMessage.trim();
    if (!text || sending || !connectionId) return;

    setSending(true);
    setNewMessage('');

    try {
      const chatRef = collection(db, 'connections', connectionId, 'planningChat');
      await addDoc(chatRef, {
        text,
        senderId: currentUserId,
        senderName: currentUserName,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('[PlanningChat] Error sending message:', error);
      setNewMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [newMessage, sending, connectionId, currentUserId, currentUserName]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const unreadCount = messages.filter(
    (m) => m.senderId !== currentUserId
  ).length;

  return (
    <div
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#fff',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 12px',
          background: isExpanded ? '#EFF6FF' : '#F9FAFB',
          border: 'none',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ChatCircle size={16} weight="bold" color="#3B82F6" />
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
            Planning Chat
          </span>
          {!isExpanded && messages.length > 0 && (
            <span
              style={{
                fontSize: '10px',
                background: '#3B82F6',
                color: '#fff',
                borderRadius: '10px',
                padding: '1px 6px',
                fontWeight: 600,
              }}
            >
              {messages.length}
            </span>
          )}
        </div>
        <span style={{ fontSize: '10px', color: '#9CA3AF' }}>
          {isExpanded ? 'Inklappen' : 'Uitklappen'}
        </span>
      </button>

      {/* Messages area */}
      {isExpanded && (
        <>
          <div
            style={{
              maxHeight: '160px',
              overflowY: 'auto',
              padding: '8px',
              background: '#FAFAFA',
              borderTop: '1px solid #E5E7EB',
            }}
          >
            {messages.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '16px 8px',
                  color: '#9CA3AF',
                  fontSize: '11px',
                }}
              >
                Nog geen berichten. Stuur een bericht om te overleggen.
              </div>
            ) : (
              messages.map((msg) => {
                const isOwn = msg.senderId === currentUserId;
                return (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOwn ? 'flex-end' : 'flex-start',
                      marginBottom: '6px',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '80%',
                        padding: '6px 10px',
                        borderRadius: isOwn ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                        background: isOwn ? '#3B82F6' : '#E5E7EB',
                        color: isOwn ? '#fff' : '#1F2937',
                        fontSize: '12px',
                        lineHeight: '1.4',
                        wordBreak: 'break-word',
                      }}
                    >
                      {msg.text}
                    </div>
                    <span
                      style={{
                        fontSize: '9px',
                        color: '#9CA3AF',
                        marginTop: '2px',
                        padding: '0 4px',
                      }}
                    >
                      {isOwn ? 'Jij' : msg.senderName} &middot;{' '}
                      {msg.createdAt.toLocaleTimeString('nl-NL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 8px',
              borderTop: '1px solid #E5E7EB',
              background: '#fff',
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Typ een bericht..."
              disabled={sending}
              style={{
                flex: 1,
                padding: '6px 10px',
                border: '1px solid #D1D5DB',
                borderRadius: '16px',
                fontSize: '12px',
                outline: 'none',
                background: '#F9FAFB',
              }}
            />
            <button
              onClick={handleSend}
              disabled={sending || !newMessage.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: 'none',
                background: newMessage.trim() ? '#3B82F6' : '#D1D5DB',
                color: '#fff',
                cursor: newMessage.trim() ? 'pointer' : 'default',
                transition: 'background 0.2s',
                flexShrink: 0,
              }}
              title="Verstuur bericht"
            >
              <PaperPlaneRight size={14} weight="bold" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PlanningChat;
